import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Award, 
  BookOpen, 
  Calendar, 
  CheckCircle2, 
  ChevronDown, 
  Compass, 
  Flag, 
  GraduationCap,
  Heart, 
  Info, 
  Map, 
  Medal, 
  Star, 
  Target, 
  Users,
  Shield,
  Globe2 as Globe,
  AlertTriangle,
  Monitor,
  Mountain,
  Sparkles,
  TreePine
} from 'lucide-react';
import { useTranslation } from '../Context/TranslationContext';

const RangerSection = () => {
  const { isTamil } = useTranslation();
  const [expandedSection, setExpandedSection] = useState(null);

  const overviewContent = {
    intro: {
      en: "The Ranger section is the senior most wing for girls aged 15 to 25 years, focused on advanced leadership development, specialized service projects, and profound community impact through the Rangers Team.",
      ta: "ரேஞ்சர் பிரிவு 15 முதல் 25 வயது வரையிலான பெண்களுக்கான மூத்த பிரிவாகும், ரேஞ்சர்ஸ் குழு மூலம் மேம்பட்ட தலைமைத்துவ மேம்பாடு, சிறப்பு சேவை திட்டங்கள் மற்றும் ஆழமான சமூக தாக்கத்தில் கவனம் செலுத்துகிறது."
    },
    keyFeatures: [
      {
        title: { en: "Age Group", ta: "வயது பிரிவு" },
        content: { en: "15 to 25 years", ta: "15 முதல் 25 வயது வரை" },
        icon: Users
      },
      {
        title: { en: "Unit Name", ta: "அலகு பெயர்" },
        content: { en: "Ranger Team", ta: "ரேஞ்சர் அணி" },
        icon: Flag
      },
      {
        title: { en: "Motto", ta: "குறிக்கோள்" },
        content: { en: "Service", ta: "சேவை" },
        icon: Heart
      }
    ],
    objectives: [
      {
        title: { en: "Leadership Excellence", ta: "தலைமைத்துவ சிறப்பு" },
        content: { 
          en: "Developing advanced leadership skills and mentoring capabilities for community impact",
          ta: "சமூக தாக்கத்திற்கான மேம்பட்ட தலைமைத்துவ திறன்கள் மற்றும் வழிகாட்டல் திறன்களை வளர்த்தல்"
        }
      },
      {
        title: { en: "Specialized Service", ta: "சிறப்பு சேவை" },
        content: {
          en: "Executing high-impact community projects and disaster management initiatives",
          ta: "அதிக தாக்கமுள்ள சமூக திட்டங்கள் மற்றும் பேரிடர் மேலாண்மை முயற்சிகளை செயல்படுத்துதல்"
        }
      },
      {
        title: { en: "Professional Growth", ta: "தொழில்முறை வளர்ச்சி" },
        content: {
          en: "Developing career-oriented skills and advanced outdoor expertise",
          ta: "வேலைவாய்ப்பு சார்ந்த திறன்கள் மற்றும் மேம்பட்ட வெளிப்புற நிபுணத்துவத்தை வளர்த்தல்"
        }
      }
    ]
  };

  const sectionTitles = {
    overview: {
      title: { en: "Overview", ta: "கண்ணோட்டம்" },
    },
    advancement: {
      title: { en: "Advancement Scheme", ta: "முன்னேற்றத் திட்டம்" },
    },
    proficiencyBadges: {
      title: { en: "Proficiency Badges", ta: "திறன் பதக்கங்கள்" },
    }
  };

  const advancementScheme = [
    {
      title: { en: "Pravesh", ta: "பிரவேஷ்" },
      duration: { en: "Initial Stage", ta: "ஆரம்ப நிலை" },
      requirements: [
        { en: "Understanding Ranger fundamentals", ta: "ரேஞ்சர் அடிப்படைகளை புரிந்துகொள்ளுதல்" },
        { en: "Community service introduction", ta: "சமூக சேவை அறிமுகம்" },
        { en: "Basic leadership principles", ta: "அடிப்படை தலைமைத்துவ கொள்கைகள்" },
        { en: "Team building activities", ta: "அணி உருவாக்க செயல்பாடுகள்" }
      ],
      icon: Flag
    },
    {
      title: { en: "Nipun", ta: "நிபுண்" },
      duration: { en: "6 months", ta: "6 மாதங்கள்" },
      requirements: [
        { en: "Advanced outdoor skills", ta: "மேம்பட்ட வெளிப்புற திறன்கள்" },
        { en: "Project management", ta: "திட்ட மேலாண்மை" },
        { en: "Specialized training", ta: "சிறப்பு பயிற்சி" },
        { en: "Service project leadership", ta: "சேவை திட்ட தலைமைத்துவம்" }
      ],
      icon: Mountain
    },
    {
      title: { en: "Rajya Puraskar", ta: "ராஜ்ய புரஸ்கார்" },
      duration: { en: "12 months", ta: "12 மாதங்கள்" },
      requirements: [
        { en: "Community development projects", ta: "சமூக மேம்பாட்டு திட்டங்கள்" },
        { en: "Advanced leadership roles", ta: "மேம்பட்ட தலைமைத்துவ பங்குகள்" },
        { en: "Specialized skill training", ta: "சிறப்பு திறன் பயிற்சி" },
        { en: "Training younger sections", ta: "இளைய பிரிவுகளுக்கு பயிற்சி" }
      ],
      icon: Award
    },
    {
      title: { en: "President's Ranger", ta: "குடியரசுத் தலைவர் ரேஞ்சர்" },
      special: true,
      duration: { en: "Final Achievement", ta: "இறுதி சாதனை" },
      requirements: [
        { en: "Excellence in leadership", ta: "தலைமைத்துவத்தில் சிறப்பு" },
        { en: "High-impact community projects", ta: "அதிக தாக்கமுள்ள சமூக திட்டங்கள்" },
        { en: "Mentoring junior sections", ta: "இளைய பிரிவுகளுக்கு வழிகாட்டுதல்" },
        { en: "Special service initiatives", ta: "சிறப்பு சேவை முயற்சிகள்" }
      ],
      icon: Sparkles
    }
  ];

  const proficiencyBadges = [
    {
      category: { en: "Leadership Skills", ta: "தலைமைத்துவ திறன்கள்" },
      badges: [
        { name: { en: "Project Manager", ta: "திட்ட மேலாளர்" }, icon: Target },
        { name: { en: "Community Leader", ta: "சமூக தலைவர்" }, icon: Users },
        { name: { en: "Trainer", ta: "பயிற்சியாளர்" }, icon: GraduationCap }
      ]
    },
    {
      category: { en: "Advanced Skills", ta: "மேம்பட்ட திறன்கள்" },
      badges: [
        { name: { en: "Disaster Management", ta: "பேரிடர் மேலாண்மை" }, icon: AlertTriangle },
        { name: { en: "Adventure Expert", ta: "சாகச நிபுணர்" }, icon: Mountain },
        { name: { en: "Environment Specialist", ta: "சுற்றுச்சூழல் நிபுணர்" }, icon: TreePine }
      ]
    },
    {
      category: { en: "Service Specialization", ta: "சேவை சிறப்பியல்பு" },
      badges: [
        { name: { en: "Social Impact", ta: "சமூக தாக்கம்" }, icon: Globe },
        { name: { en: "Healthcare Leader", ta: "சுகாதார தலைவர்" }, icon: Heart },
        { name: { en: "Education Mentor", ta: "கல்வி வழிகாட்டி" }, icon: BookOpen }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 mt-16">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#8B0000] to-[#800020] py-20">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center text-white"
          >
            <h1 className="text-5xl font-bold mb-6">
              {isTamil ? "ரேஞ்சர் பிரிவு" : "Ranger Section"}
            </h1>
            <p className="text-xl opacity-90 max-w-3xl mx-auto">
              {overviewContent.intro[isTamil ? 'ta' : 'en']}
            </p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4">
        {/* Overview Section */}
        <section className="py-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {sectionTitles.overview.title[isTamil ? 'ta' : 'en']}
            </h2>
            <div className="w-20 h-1 bg-[#8B0000] mx-auto"></div>
          </div>

          {/* Key Features */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {overviewContent.keyFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 shadow-lg"
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-3 bg-[#8B0000] bg-opacity-10 rounded-lg text-[#8B0000]">
                    <feature.icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-xl font-semibold">
                    {feature.title[isTamil ? 'ta' : 'en']}
                  </h3>
                </div>
                <p className="text-gray-600">
                  {feature.content[isTamil ? 'ta' : 'en']}
                </p>
              </motion.div>
            ))}
          </div>

          {/* Objectives */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {overviewContent.objectives.map((objective, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 shadow-lg"
              >
                <h3 className="text-xl font-semibold mb-4">
                  {objective.title[isTamil ? 'ta' : 'en']}
                </h3>
                <p className="text-gray-600">
                  {objective.content[isTamil ? 'ta' : 'en']}
                </p>
              </motion.div>
            ))}
          </div>
        </section>

   {/* Advancement Section */}
   <section className="py-16 bg-white">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {sectionTitles.advancement.title[isTamil ? 'ta' : 'en']}
            </h2>
            <div className="w-20 h-1 bg-[#8B0000] mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {advancementScheme.map((level, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`bg-white rounded-xl shadow-lg p-6 ${level.special ? 'border-2 border-yellow-500' : ''}`}
              >
                <div className="flex items-center gap-4 mb-4">
                  <div className={`p-3 rounded-lg ${level.special ? 'bg-yellow-50 text-yellow-600' : 'bg-[#8B0000] bg-opacity-10 text-[#8B0000]'}`}>
                    <level.icon className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">
                      {level.title[isTamil ? 'ta' : 'en']}
                    </h3>
                    {level.duration && (
                      <p className="text-sm text-gray-500">
                        {level.duration[isTamil ? 'ta' : 'en']}
                      </p>
                    )}
                  </div>
                </div>
                <ul className="space-y-2">
                  {level.requirements.map((req, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">
                        {req[isTamil ? 'ta' : 'en']}
                      </span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </section>
        {/* Proficiency Badges Section */}
        <section className="py-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {sectionTitles.proficiencyBadges.title[isTamil ? 'ta' : 'en']}
            </h2>
            <div className="w-20 h-1 bg-[#8B0000] mx-auto"></div>
          </div>

          <div className="space-y-8">
            {proficiencyBadges.map((category, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-2xl font-semibold mb-6">
                  {category.category[isTamil ? 'ta' : 'en']}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {category.badges.map((badge, bIndex) => (
                    <div key={bIndex} className="flex items-center gap-4 bg-gray-50 rounded-lg p-4">
                      <div className="p-2 bg-[#8B0000] bg-opacity-10 rounded-lg text-[#8B0000]">
                        <badge.icon className="w-5 h-5" />
                      </div>
                      <span className="text-gray-700">
                        {badge.name[isTamil ? 'ta' : 'en']}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default RangerSection;